import { Alert, Card, SkeletonRows } from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { DeliveryDespatchForm } from "../../../../features/fuel/delivery-form.js";
import { useFuelOrder, useFuelPermissions } from "../../../../features/fuel/queries.js";
import {
  FuelPage,
  fuelSearchShape,
  NoStations,
  StationField,
  useFuelStation,
} from "../../../../features/fuel/shared.js";
import { errorMessage } from "../../../../lib/errors.js";
import { z } from "zod";

const searchSchema = z.object({
  ...fuelSearchShape,
  orderId: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/akaryakit/dolumlar/yeni")({
  validateSearch: (search) => searchSchema.parse(search),
  component: NewDeliveryPage,
});

/**
 * Yeni tanker dolumu (sihirbazın 1. adımı). `?orderId=` ile gelindiyse dağıtıcı ve ürün satırları
 * siparişten önerilir; boşaltılacak tanklar her hâlükârda burada seçilir.
 */
function NewDeliveryPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useFuelPermissions();
  return (
    <FuelPage
      title={t("fuel.deliveries.new.title")}
      description={t("fuel.deliveries.new.description")}
      breadcrumb={[
        {
          label: t("menu.fuelDeliveries"),
          link: (children, className) => (
            <Link to="/akaryakit/dolumlar" className={className}>
              {children}
            </Link>
          ),
        },
        { label: t("fuel.deliveries.new.title") },
      ]}
    >
      {({ companyId }) =>
        permissions.canManageDeliveries ? (
          <NewDeliveryBody companyId={companyId} />
        ) : (
          <Alert tone="info">{t("fuel.deliveries.new.noPermission")}</Alert>
        )
      }
    </FuelPage>
  );
}

function NewDeliveryBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const station = useFuelStation(companyId);
  const order = useFuelOrder(search.orderId ?? null);

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;
  if (search.orderId && order.isPending) return <SkeletonRows rows={6} />;

  // Sipariş başka bir istasyona aitse dolum o istasyona açılır (kullanıcı seçimi değil, belge belirler).
  const stationId = order.data?.stationId ?? station.stationId;

  return (
    <>
      {station.showPicker && !order.data ? (
        <Card className="flex flex-wrap items-end gap-3 p-4">
          <StationField selection={station} />
        </Card>
      ) : null}
      {order.error ? <Alert tone="warning">{t("fuel.deliveries.new.orderMissing")}</Alert> : null}
      <DeliveryDespatchForm
        key={stationId}
        companyId={companyId}
        stationId={stationId}
        delivery={null}
        order={
          order.data
            ? {
                id: order.data.id,
                orderNo: order.data.orderNo,
                distributorCounterpartyId: order.data.distributorCounterpartyId,
                lines: order.data.lines.map((line) => ({
                  productId: line.productId,
                  quantityL: line.quantityL,
                })),
              }
            : null
        }
        onSaved={(delivery) => void navigate({ to: "/akaryakit/dolumlar/$id", params: { id: delivery.id } })}
      />
    </>
  );
}
